package com.partejar.app.core.address

import com.partejar.app.feature.gestantes.data.GestanteAddress
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.http.HttpStatusCode
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

interface CepDataSource {
    /** Null means a valid CEP was not found. Network failures are propagated. */
    suspend fun lookup(cep: String): CepAddress?
}

data class CepAddress(val street: String, val neighborhood: String, val city: String, val state: String)

// This client has no authentication plugin: app session tokens never go to ViaCEP.
fun createCepHttpClient() = HttpClient {
    install(ContentNegotiation) { json(Json { ignoreUnknownKeys = true }) }
    install(HttpTimeout) { requestTimeoutMillis = 10000 }
}

class ViaCepDataSource(private val client: HttpClient) : CepDataSource {
    override suspend fun lookup(cep: String): CepAddress? {
        require(cep.length == 8 && cep.all { it in '0'..'9' })
        val response = client.get("https://viacep.com.br/ws/$cep/json/")
        check(response.status == HttpStatusCode.OK) { "Consulta de CEP indisponível" }
        val result = response.body<ViaCepResponse>()
        if (result.erro) return null
        return CepAddress(result.logradouro, result.bairro, result.localidade, result.uf)
    }
}

@Serializable
private data class ViaCepResponse(
    val logradouro: String = "",
    val bairro: String = "",
    val localidade: String = "",
    val uf: String = "",
    val erro: Boolean = false
)

/** Keep edits made while the request was running, and never replace number/complement. */
internal fun GestanteAddress.withCepResult(start: GestanteAddress, result: CepAddress): GestanteAddress {
    if (cep != start.cep) return this
    fun field(current: String?, original: String?, fetched: String): String? =
        if (current == original && fetched.isNotBlank()) fetched else current
    return copy(
        street = field(street, start.street, result.street),
        neighborhood = field(neighborhood, start.neighborhood, result.neighborhood),
        city = field(city, start.city, result.city),
        state = field(state, start.state, result.state)
    )
}
